import { useMemo } from "react";
import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  AXIS_COLOR,
  AXIS_TICK,
  GRID_COLOR,
  yAxisTitle,
} from "../../UI/Chart/chartStyle.ts";
import { ChartLegendItem } from "../../UI/Chart/ChartLegendItem.tsx";
import { WeeklyOnboardingLives } from "./onboardingLiveStatistics.ts";
import { ATTENDED_COLOR, ENROLLED_COLOR } from "./statisticsColors.ts";

const HOVERED_WEEK_WASH = "#f0efec";

type WeeklyPoint = WeeklyOnboardingLives & { label: string };

function weekStartOf(point: WeeklyOnboardingLives) {
  return dayjs(point.weekStartDay).locale("it");
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color?: string;
  label: string;
  value: number;
}) {
  return (
    <div className={"flex items-center justify-between gap-3"}>
      <span className={"flex items-center gap-1.5 text-slate-500"}>
        {color && (
          <span
            className={"w-2.5 h-2.5 rounded-sm"}
            style={{ backgroundColor: color }}
          />
        )}
        {label}
      </span>
      <span className={"font-semibold text-slate-800"}>{value}</span>
    </div>
  );
}

function WeeklyTooltip({ active, payload }: TooltipProps<number, string>) {
  const week = payload?.[0]?.payload as WeeklyPoint | undefined;
  if (!active || !week) return null;
  const weekStart = weekStartOf(week);
  return (
    <div
      className={
        "bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs flex flex-col gap-1.5 min-w-44"
      }
    >
      <span className={"font-semibold text-slate-700"}>
        {weekStart.format("D MMM")} – {weekStart.add(6, "day").format("D MMM")}
      </span>
      <TooltipRow label={"Live svolte"} value={week.lives} />
      <TooltipRow
        color={ENROLLED_COLOR}
        label={"Iscritti"}
        value={week.enrolled}
      />
      <TooltipRow
        color={ATTENDED_COLOR}
        label={"Partecipanti (feedback)"}
        value={week.attended}
      />
    </div>
  );
}

export function OnboardingLiveWeeklyTrendChart({
  weeks,
}: {
  weeks: WeeklyOnboardingLives[];
}) {
  const points = useMemo<WeeklyPoint[]>(
    () =>
      weeks.map((week) => ({
        ...week,
        label: weekStartOf(week).format("D MMM"),
      })),
    [weeks],
  );

  return (
    <div className={"flex flex-col gap-2"}>
      <div className={"flex flex-wrap items-center justify-end gap-4"}>
        <ChartLegendItem
          color={ENROLLED_COLOR}
          label={"Iscritti"}
          shape={"block"}
        />
        <ChartLegendItem
          color={ATTENDED_COLOR}
          label={"Partecipanti (feedback)"}
          shape={"block"}
        />
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart
          data={points}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} stroke={GRID_COLOR} />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: AXIS_COLOR }}
            interval={"preserveStartEnd"}
            minTickGap={16}
          />
          <YAxis
            allowDecimals={false}
            label={yAxisTitle("Studenti")}
            width={48}
            tick={AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<WeeklyTooltip />}
            cursor={{ fill: HOVERED_WEEK_WASH }}
          />
          <Bar
            dataKey="enrolled"
            name="Iscritti"
            fill={ENROLLED_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
          />
          <Bar
            dataKey="attended"
            name="Partecipanti (feedback)"
            fill={ATTENDED_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
